import { LegalLayout, LegalSection } from "./LegalPage";

/**
 * Terms of Service — standard SaaS template tailored to UAIP (OAuth sign-in,
 * crawling of user-supplied web apps, tiered subscriptions via Stripe).
 */
export function TermsPage(): JSX.Element {
  return (
    <LegalLayout title="Terms of Service" updated="3 July 2026">
      <p>
        These Terms of Service ("Terms") govern your access to and use of UAIP —
        the UI Automation Intelligence Platform ("UAIP", "the Service"),
        operated by NNG IT Solutions ("we", "us", "our"). By creating an account
        or using the Service, you agree to these Terms. If you do not agree, do
        not use the Service.
      </p>

      <LegalSection heading="1. The Service">
        <p>
          UAIP crawls web applications you point it at, analyses them, and
          generates automated test code (Playwright, Selenium, Cypress, UiPath)
          that you own. We may add, change, or remove features over time.
        </p>
      </LegalSection>

      <LegalSection heading="2. Accounts &amp; eligibility">
        <p>
          You sign in through a third-party identity provider (Google or GitHub)
          via OAuth. We never see or store your provider password. You are
          responsible for activity under your account and for keeping your
          provider account secure. You must be at least 16 years old and able to
          form a binding contract to use the Service.
        </p>
      </LegalSection>

      <LegalSection heading="3. Acceptable use">
        <p>You agree that you will only use UAIP to crawl and test:</p>
        <ul className="list-disc space-y-1 pl-5">
          <li>web applications you own, or</li>
          <li>
            web applications you have explicit authorisation to test on behalf
            of their owner.
          </li>
        </ul>
        <p>
          You must not use the Service to access, scan, or attack any system
          without authorisation, to violate any law, to infringe others' rights,
          or to disrupt or overload the Service or any target site. You are
          solely responsible for ensuring your use is lawful and authorised.
        </p>
      </LegalSection>

      <LegalSection heading="4. Your content and test output">
        <p>
          You retain all rights to the applications you test and the test code
          UAIP generates for you. Generated tests are yours to keep, run, and
          modify — including after you cancel. You grant us the limited right to
          process your inputs and store outputs only as needed to operate the
          Service for you.
        </p>
      </LegalSection>

      <LegalSection heading="5. Plans, billing &amp; cancellation">
        <p>
          UAIP offers a free tier and paid subscription plans. Paid plans are
          billed monthly in advance through our payment processor, Stripe. Fees
          are non-refundable except where required by law. You can cancel at any
          time from your account; access continues until the end of the current
          billing period, after which the account reverts to the free tier. We
          may change pricing with reasonable notice; changes do not affect the
          period you have already paid for.
        </p>
      </LegalSection>

      <LegalSection heading="6. Third-party integrations">
        <p>
          If you connect third-party services (e.g. GitHub, Slack, Jira,
          Linear), you authorise us to interact with them on your behalf using
          the credentials or tokens you provide. Your use of those services is
          governed by their own terms, and we are not responsible for them.
        </p>
      </LegalSection>

      <LegalSection heading="7. Intellectual property">
        <p>
          The Service itself — including its software, design, and branding — is
          owned by us and our licensors. These Terms grant you a limited,
          non-exclusive, non-transferable right to use the Service; they do not
          transfer any ownership of the Service to you.
        </p>
      </LegalSection>

      <LegalSection heading="8. Disclaimers">
        <p>
          The Service is provided "as is" and "as available", without warranties
          of any kind, whether express or implied, including fitness for a
          particular purpose and non-infringement. We do not warrant that
          generated tests will be error-free or that the Service will be
          uninterrupted. You are responsible for reviewing generated code before
          relying on it.
        </p>
      </LegalSection>

      <LegalSection heading="9. Limitation of liability">
        <p>
          To the maximum extent permitted by law, we will not be liable for any
          indirect, incidental, special, consequential, or punitive damages, or
          for any loss of data, profits, or revenue. Our total liability arising
          out of or relating to the Service is limited to the amount you paid us
          in the twelve months before the event giving rise to the claim.
        </p>
      </LegalSection>

      <LegalSection heading="10. Termination">
        <p>
          You may stop using the Service at any time. We may suspend or terminate
          your access if you breach these Terms or use the Service unlawfully.
          You may request deletion of your account and associated data at any
          time (see the Privacy Policy).
        </p>
      </LegalSection>

      <LegalSection heading="11. Changes to these Terms">
        <p>
          We may update these Terms from time to time. If we make material
          changes, we will take reasonable steps to notify you. Your continued
          use of the Service after changes take effect constitutes acceptance.
        </p>
      </LegalSection>

      <LegalSection heading="12. Governing law">
        <p>
          These Terms are governed by the laws of Malaysia, and the courts of
          Kuala Lumpur have exclusive jurisdiction over any dispute, without
          regard to conflict-of-laws principles.
        </p>
      </LegalSection>

      <LegalSection heading="13. Contact">
        <p>
          Questions about these Terms? Email{" "}
          <a className="text-slate-200 hover:text-white" href="mailto:info@uaip.dev">
            info@uaip.dev
          </a>
          .
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
